import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LoginPage, ForgotPasswordPage, ResetPasswordPage } from './components/Login';
import {
  CreateAccountPage,
  SelectSectorsPage,
  VerifyEmailPage,
  CompleteProfileStep1Page,
  CompleteProfileStep2Page,
} from './components/Signup';
import { LearnerDashboard } from './components/LearnerDashboard';
import { ProtectedRoute } from './components/ProtectedRoute';

// Redirects unknown paths (e.g. /oauth2/authorize?...) to /login,
// forwarding the full original URL as the ?next= param so the login
// page can complete the OAuth2 handshake after authentication.
function RedirectToLogin() {
  const location = useLocation();
  const fullPath = location.pathname + location.search + location.hash;
  const loginUrl = '/login?next=' + encodeURIComponent(fullPath);
  return <Navigate to={loginUrl} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Get basename from Vite's base config (e.g., "/authn" in production)
const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={basename}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* Password reset link target. The backend's CENTRIX_PASSWORD_RESET_PATH
              defaults to /password-reset/{uid}/{token}/ (trailing slash). */}
          <Route path="/password-reset/:uid/:token" element={<ResetPasswordPage />} />
          <Route path="/password-reset/:uid/:token/" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<CreateAccountPage />} />
          <Route path="/signup/select-sectors" element={<SelectSectorsPage />} />
          <Route path="/signup/verify-email" element={<VerifyEmailPage />} />
          <Route path="/signup/complete-profile" element={<CompleteProfileStep1Page />} />
          <Route path="/signup/complete-profile/learners" element={<CompleteProfileStep2Page />} />
          <Route
            path="/learner-dashboard"
            element={
              <ProtectedRoute>
                <LearnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<RedirectToLogin />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
